/**
 * Keeps the on-device focus log and the backend's in step.
 *
 * Pull: the last 90 days on mount and on window focus, merged into the local
 * log, so a block finished on the phone shows up on the laptop.
 * Push: every unsynced entry, up to `MAX_BLOCKS_PER_SYNC` per request, until
 * none are left. A failure keeps them on the device and retries on the next
 * change, on reconnect, or after `RETRY_MS`. A 4xx other than auth or rate
 * limiting means the server will never take that batch, so it is marked
 * rejected rather than retried forever.
 *
 * The rules themselves are pure and tested in `lib/focus/focusSync`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError, wellbeingApi } from '@/lib/api-client';
import { queryKeys } from '@/config/query-client';
import type { FocusLogEntry } from '@/lib/focus/focusLog';
import {
  MAX_BLOCKS_PER_SYNC,
  markSent,
  mergeRemote,
  pendingUploads,
  syncWindowStart,
  toUpload,
} from '@/lib/focus/focusSync';

export const RETRY_MS = 60_000;

/**
 * - `off`: nothing to sync with (signed out, or a mock/empty data mode).
 * - `saving`: an upload is in flight.
 * - `offline` / `error`: blocks are waiting on this device.
 */
export type FocusSyncState = 'off' | 'idle' | 'saving' | 'offline' | 'error';

export interface FocusSyncStatus {
  state: FocusSyncState;
  /** Blocks on this device the server does not have yet. */
  pending: number;
}

const RETRYABLE = new Set([401, 403, 408, 429]);

function isPermanent(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    !RETRYABLE.has(error.status)
  );
}

const isOnline = () =>
  typeof navigator === 'undefined' || navigator.onLine !== false;

function useOnline(): boolean {
  const [online, setOnline] = useState(isOnline);
  useEffect(() => {
    const update = () => setOnline(isOnline());
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}

interface SyncArgs {
  enabled: boolean;
  userId: string;
  log: FocusLogEntry[];
  setLog: (log: FocusLogEntry[]) => void;
}

function usePull({ enabled, userId, log, setLog }: SyncArgs) {
  const latest = useRef(log);
  latest.current = log;
  const { data } = useQuery({
    queryKey: queryKeys.wellbeing.focusBlocks(userId),
    queryFn: () => wellbeingApi.listFocusBlocks(syncWindowStart(Date.now())),
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
  useEffect(() => {
    if (!data) return;
    const merged = mergeRemote(latest.current, data.blocks, Date.now());
    if (merged !== latest.current) setLog(merged);
  }, [data, setLog]);
}

function usePush({ enabled, log, setLog }: SyncArgs, online: boolean) {
  const latest = useRef(log);
  latest.current = log;
  const pending = useMemo(() => pendingUploads(log), [log]);
  // Changes whenever what is waiting changes, including a recap edit.
  const pendingKey = pending.map(e => `${e.id}:${e.recap ?? ''}`).join('|');
  const inFlight = useRef(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const goAgain = useCallback(() => setAttempt(n => n + 1), []);

  useEffect(() => {
    if (!enabled || !online || !pendingKey || inFlight.current) return;
    const sent = pendingUploads(latest.current)
      .slice(0, MAX_BLOCKS_PER_SYNC)
      .map(toUpload);
    let retry: ReturnType<typeof setTimeout> | undefined;
    inFlight.current = true;
    setSaving(true);
    wellbeingApi
      .syncFocusBlocks(sent)
      .then(({ saved }) => {
        inFlight.current = false;
        setFailed(false);
        setLog(markSent(latest.current, sent, saved, 'synced'));
        // Go again at once: there may be more than one batch, or a recap
        // typed while this one was in flight.
        goAgain();
      })
      .catch(error => {
        inFlight.current = false;
        if (isPermanent(error)) {
          setFailed(false);
          const ids = sent.map(u => u.id);
          setLog(markSent(latest.current, sent, ids, 'rejected'));
          goAgain();
          return;
        }
        setFailed(true);
        retry = setTimeout(goAgain, RETRY_MS);
      })
      .finally(() => setSaving(false));
    return () => clearTimeout(retry);
  }, [enabled, online, pendingKey, attempt, setLog, goAgain]);

  return { pending: pending.length, saving, failed };
}

export function useFocusLogSync(args: SyncArgs): FocusSyncStatus {
  const online = useOnline();
  usePull(args);
  const { pending, saving, failed } = usePush(args, online);

  if (!args.enabled) return { state: 'off', pending: 0 };
  if (saving) return { state: 'saving', pending };
  if (pending === 0) return { state: 'idle', pending };
  if (!online) return { state: 'offline', pending };
  return { state: failed ? 'error' : 'idle', pending };
}
